const TEXT_FRAGMENT_SELECTOR_REGEX = /#:~:text=[^\s)\]"]*/g
const MARKDOWN_URL_LABEL_REGEX = /\[[^\]]*\.[a-z]{2,}[^\]]*\]\((https?:\/\/[^\s)]+)\)/gi
const MARKDOWN_PROTOCOL_LABEL_REGEX = /\[(?:https?:\/\/|www\.)[^\]]+\]\((https?:\/\/[^)]+)\)/g
const CARET_PAREN_CITATION_REGEX = /\(\s*\^[^)]*\)/g
const CARET_BRACKET_CITATION_REGEX = /\[\s*\^[^\]]*\]/g
const EMPTY_PARENS_REGEX = /\(\s*\)/g
const RAW_URL_REGEX = /(?<!\()(https?:\/\/[^\s)\]"]+)/g
const HARD_BREAK_TOKEN = '__PDF_HARD_BREAK__'

const LIST_ITEM_REGEX = /^([-*+]|\d+\.)\s+/
const BLOCKQUOTE_REGEX = /^>\s+/
const STRUCTURAL_MARKDOWN_LINE_REGEX =
  /^(#{1,6}\s|[-*+]\s|\d+\.\s|>\s|```|~~~|\||-{3,}\s*$|\*{3,}\s*$|_{3,}\s*$)/

export function stripMarkdownGfmHazards(raw: string): string {
  if (!raw) return ''

  return raw
    .replace(TEXT_FRAGMENT_SELECTOR_REGEX, '')
    .replace(MARKDOWN_URL_LABEL_REGEX, '[↗]($1)')
    .replace(MARKDOWN_PROTOCOL_LABEL_REGEX, '[↗]($1)')
    .replace(CARET_PAREN_CITATION_REGEX, '')
    .replace(CARET_BRACKET_CITATION_REGEX, '')
    .replace(EMPTY_PARENS_REGEX, '')
    .replace(RAW_URL_REGEX, '')
}

export function stripTextFragmentSelectors(raw: string): string {
  if (!raw) return ''
  return raw.replace(TEXT_FRAGMENT_SELECTOR_REGEX, '')
}

export function cleanInlineMarkdownForGfm(raw: string): string {
  return stripMarkdownGfmHazards(raw)
    .replace(/[ \t\r\n]+/g, ' ')
    .trim()
}

function isStructuralMarkdownLine(line: string): boolean {
  const trimmed = line.trim()
  return Boolean(trimmed) && STRUCTURAL_MARKDOWN_LINE_REGEX.test(trimmed)
}

function shouldJoinMarkdownLines(previousLine: string, nextLine: string): boolean {
  const previousTrimmed = previousLine.trim()
  const nextTrimmed = nextLine.trim()

  if (!previousTrimmed || !nextTrimmed) {
    return false
  }

  if (previousLine.includes(HARD_BREAK_TOKEN)) {
    return false
  }

  if (previousLine.endsWith('  ') || previousLine.endsWith('\\')) {
    return false
  }

  if (BLOCKQUOTE_REGEX.test(previousTrimmed) && BLOCKQUOTE_REGEX.test(nextTrimmed)) {
    return true
  }

  if (LIST_ITEM_REGEX.test(previousTrimmed) && !isStructuralMarkdownLine(nextTrimmed)) {
    return true
  }

  if (isStructuralMarkdownLine(previousTrimmed) || isStructuralMarkdownLine(nextTrimmed)) {
    return false
  }

  return true
}

export function normalizeBlockMarkdownWhitespace(raw: string): string {
  if (!raw) return ''

  const normalized = raw
    .replace(/\r\n/g, '\n')
    .replace(/<br\s*\/?>/gi, `${HARD_BREAK_TOKEN}\n`)
    .replace(/\u00a0/g, ' ')

  const lines = normalized.split('\n')
  const output: string[] = []
  let inFence = false

  lines.forEach((line) => {
    const normalizedLine = line.replace(/\t/g, '  ').replace(/[ \t]+$/g, '')
    const trimmed = normalizedLine.trim()

    if (/^(```|~~~)/.test(trimmed)) {
      inFence = !inFence
      output.push(normalizedLine)
      return
    }

    if (inFence) {
      output.push(normalizedLine)
      return
    }

    const previousLine = output[output.length - 1]
    if (previousLine != null && shouldJoinMarkdownLines(previousLine, normalizedLine)) {
      output[output.length - 1] = `${previousLine.trimEnd()} ${trimmed}`
      return
    }

    output.push(normalizedLine)
  })

  return output
    .join('\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(new RegExp(HARD_BREAK_TOKEN, 'g'), '  ')
    .trim()
}

export function cleanBlockMarkdownForGfm(raw: string): string {
  return normalizeBlockMarkdownWhitespace(stripMarkdownGfmHazards(raw))
}

export function cleanPdfMarkdown(raw: string): string {
  if (!raw) return ''

  return stripTextFragmentSelectors(raw)
    .replace(/\r\n/g, '\n')
    .replace(/<br\s*\/?>/gi, '  \n')
    .replace(/\u00a0/g, ' ')
    .trim()
}
